import { Report, PricebookReport, SummedReport } from '../../../models/ReportData';
import { AccountCounts, AccountStatusKeys, UserAccountsTableData } from './userAccountsUtils';
import { findPricebookReportsByType } from './pricebookReportUtils';

const sumNestedPropertyRecursively = (
  obj: { [key: string]: unknown },
  property: string,
): number => {
  if (typeof obj !== 'object' || obj === null) return 0;

  let sum = 0;
  if (property in obj && typeof obj[property] === 'number') {
    sum += obj[property];
  }

  for (const key in obj) {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      sum += sumNestedPropertyRecursively(obj[key] as { [key: string]: unknown }, property);
    }
  }

  return sum;
};

function sumAccountCountsByStatus(data: UserAccountsTableData, keys: AccountStatusKeys[]): number {
  return keys.reduce((sum, key) => sum + (data.Total[key] || 0), 0);
}

const calculatePricebookAccountCounts = (pricebookReport: PricebookReport): AccountCounts => {
  return {
    activeUserCount: pricebookReport.activeUserCount || 0,
    ineligibleCount: pricebookReport.ineligibleCount || 0,
    freeTrialPaymentPlatformCount: pricebookReport.freeTrialPaymentPlatformCount || 0,
    unsubscribedCount: pricebookReport.unsubscribedCount || 0,
    abandonedCount: 0,
    invitedCount: 0,
    undecidedCount: 0,
    activeAddOnCount: pricebookReport.activeAddOnCount || 0,
    unsubscribedFromAddOnCount: pricebookReport.unsubscribedFromAddOnCount || 0,
  };
};

const calculateUnknownAccountCounts = (report: Report): AccountCounts => {
  return {
    activeUserCount: 0,
    abandonedCount: report.abandonedCount || 0,
    ineligibleCount: report.unknownIneligibleCount || 0,
    invitedCount: report.invitedCount || 0,
    freeTrialPaymentPlatformCount: 0,
    unsubscribedCount: 0,
    undecidedCount: report.undecidedCount || 0,
    activeAddOnCount: 0,
    unsubscribedFromAddOnCount: 0,
  };
};

const getActiveUserCount = (report: SummedReport | null): number => {
  if (!report) return 0;

  const allPricebookReports = findPricebookReportsByType(report);
  const totalPricebookReport = allPricebookReports.find(
    r => r.productType && (r.productType as string).toUpperCase() === 'TOTAL',
  );

  if (totalPricebookReport) {
    return totalPricebookReport.activeUserCount || 0;
  }

  return allPricebookReports.reduce((sum, report) => {
    const value = report.activeUserCount;
    return sum + (typeof value === 'number' ? value : 0);
  }, 0);
};

export {
  sumNestedPropertyRecursively as sumNestedProperty,
  sumAccountCountsByStatus,
  calculatePricebookAccountCounts,
  calculateUnknownAccountCounts,
  getActiveUserCount,
};
